import { alpha } from '@mui/material';
import type { ModelComponent } from '@oneview/components/~advisor/Models/ManageModels/ModelChart';
import { monthDayYearPattern } from '@oneview/constants/datePattern';
import {
    AccountSizesBreakdownStat,
    AccountTypesBreakdownStat,
    AccountWithExtraInfo,
    BalanceHistory,
    Transfer,
    TransfersDirection,
    MarketCapBreakdownStat,
    PositionAccountWithExtraInfo,
    QuoteHistory,
    QuoteStat,
    SectorsBreakdownStat,
    Stats,
    TimeRange,
    TiprankOverviewWithExtraInfo,
    TopPositionsStat,
} from '@orbis-systems/orbis-api-middleware';
import {
    divide,
    formatDate,
    formatNumber,
    formatPercentage,
    getCurrentTheme,
    multiply,
    num,
    subtract,
    sumBy,
} from '@orbis-systems/orbis-react-toolkit';
import { Chart, ChartData, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { groupBy, isEmpty, mapValues, orderBy, pick, sortBy, uniq } from 'lodash';
import { ChartType, ComparisonEtf } from '../constants/lookups';
import { getColorByKey, getConstantColor } from './colorUtils';

Chart.register(ChartDataLabels, ...registerables);

export const EMPTY_CHART_DATA: ChartData = {
    datasets: [],
    labels: [],
};

export const EMPTY_LINE_CHART_DATA = {
    labels: [formatDate(new Date()), ''],
    datasets: [
        {
            data: [0, 100],
        },
    ],
};

export function formatAccountTypesBreakdownChart(data: AccountTypesBreakdownStat): ChartData {
    return {
        labels: data.map((item) => item.label),
        datasets: [
            {
                data: data.map((item) => num(item.value).multiply(100).value()),
                borderColor: getCurrentTheme().palette.text.secondary,
                backgroundColor: data.map((item) => getConstantColor(item.label)),
            },
        ],
    };
}

export function formatAccountSizesBreakdownChart(data: AccountSizesBreakdownStat, fxRate?: number): ChartData {
    const color = getCurrentTheme().palette.primary.main;
    const rate = fxRate || 1;

    return {
        labels: data.map((item) => {
            const labelValue = Number(item.label) * rate;
            return `${labelValue.toFixed(0)}k`;
        }),
        datasets: [
            {
                data: data.map((item) => item.value),
                backgroundColor: color,
            },
        ],
    };
}

export function formatTopPositionsChartData(data: TopPositionsStat): ChartData {
    const color = getCurrentTheme().palette.secondary.main;
    return {
        labels: data.map((item) => item.label),
        datasets: [
            {
                backgroundColor: alpha(color, 0.3),
                borderColor: color,
                borderWidth: 1,
                data: data.map((item) => item.value),
            },
        ],
    };
}

export function formatGainPercentage(value: number, options: { withConversion?: boolean } = {}): string {
    const { withConversion = false } = options;

    if (!isFinite(value) || isNaN(value)) {
        return '0%';
    }

    const absValue = Math.abs(value);

    if (absValue >= 1000000) {
        const formatted = formatNumber(value, {
            decimalPlaces: absValue >= 1000000000 ? 1 : 2,
            short: true,
        });
        return `${formatted}%`;
    }

    return formatPercentage(value, {
        withConversion,
        decimalPlaces: absValue >= 1000 ? 0 : 2,
    });
}

export function formatTopAccountsChartData(accounts: AccountWithExtraInfo[]): ChartData {
    const sortedAccounts = orderBy([...accounts], (account) => account.markToMarket, 'desc').slice(0, 5);
    const color = getCurrentTheme().palette.secondary.main;

    return {
        labels: sortedAccounts.map((account) => [account.accountNumber, account.fullName]),
        datasets: [
            {
                backgroundColor: alpha(color, 0.3),
                borderColor: color,
                borderWidth: 1,
                data: sortedAccounts.map((account) => account.markToMarket),
            },
        ],
    };
}

export function formatMarketCapBreakdown(data: MarketCapBreakdownStat): ChartData {
    return {
        labels: data.map((item) => item.label),
        datasets: [
            {
                data: data.map((item) => item.value),
                borderColor: getCurrentTheme().palette.text.secondary,
                backgroundColor: data.map((item) => getColorByKey(item.label, ChartType.MarketCapBreakdown)),
            },
        ],
    };
}

export function formatSectorsBreakdownDoughnutChartWithEtf(data: SectorsBreakdownStat): ChartData {
    // Middleware will supply ETF Sectors for sectors labeled as 'Other' from Orbis api - currently only for account sectors endpoint
    const sectorLabels = data.map((sector) => sector.label);
    const sectorData = data.map((sector) => multiply(sector.value, 100));

    const groupsArr = data.map((sector) => {
        return sector.groups;
    });
    const mergedGroups: { label: string; value: number }[] = [];
    // Merging all sector groups together (ETF groups will have duplicates groups as other sectors)
    for (let i = 0; groupsArr.length !== 0; i++) {
        let j = 0;
        while (j < groupsArr.length) {
            if (i >= groupsArr[j]!.length) {
                groupsArr.splice(j, 1);
            } else {
                mergedGroups.push(groupsArr[j]![i]!);
                j += 1;
            }
        }
    }
    const results = Array.from(new Set(mergedGroups.map((s) => s.label))).map((lab) => {
        return {
            label: lab,
            value: mergedGroups
                .filter((s) => s.label === lab)
                .map((group) => group.value)
                .reduce((a, b) => a + b, 0),
        };
    });

    let groupLabels: string[] = [];
    let groupData: number[] = [];

    const labels = results.map((group) => group.label);
    const dataResults = results.map((group) => multiply(group.value, 100));
    groupLabels = groupLabels.concat(labels);
    groupData = groupData.concat(dataResults);

    const getColor = (label: string) => getColorByKey(label, ChartType.SectorsBreakdown);

    return {
        labels: [...groupLabels, ...sectorLabels],
        datasets: [
            {
                data: sectorData,
                label: 'Sectors',
                weight: 1,
                borderColor: getCurrentTheme().palette.text.secondary,
                backgroundColor: sectorLabels.map(getColor),
            },
            {
                data: groupData,
                label: 'Groups',
                weight: 3,
                borderColor: getCurrentTheme().palette.text.secondary,
                backgroundColor: groupLabels.map(getColor),
            },
        ],
    };
}

export function formatSectorsBreakdownDoughnutChart(data: SectorsBreakdownStat): ChartData {
    const sectorLabels = data.map((sector) => sector.label);
    const sectorData = data.map((sector) => multiply(sector.value, 100));

    let groupLabels: string[] = [];
    let groupData: number[] = [];

    data.forEach((sector) => {
        const labels = sector.groups.map((group) => group.label);
        const data = sector.groups.map((group) => multiply(group.value, 100));
        groupLabels = groupLabels.concat(labels);
        groupData = groupData.concat(data);
    });

    const getColor = (label: string) => getColorByKey(label, ChartType.SectorsBreakdown);

    return {
        labels: [...sectorLabels, ...groupLabels],
        datasets: [
            {
                data: sectorData,
                label: 'Sectors',
                weight: 1,
                borderColor: getCurrentTheme().palette.text.secondary,
                backgroundColor: sectorLabels.map(getColor),
            },
            {
                data: groupData,
                label: 'Groups',
                weight: 3,
                borderColor: getCurrentTheme().palette.text.secondary,
                backgroundColor: groupLabels.map(getColor),
            },
        ],
    };
}

export function formatSectorsBreakdownBarGraph(
    doughnutChartData: ChartData,
    etfs: {
        [key: string]: number;
    },
): ChartData {
    const sectorLabels = (doughnutChartData.labels || []) as string[];
    const sectorData = doughnutChartData.datasets[1]?.data || [];
    const etfSectorLabels = uniq(Object.keys(etfs).concat(sectorLabels as string[])).sort();

    return {
        labels: etfSectorLabels,
        datasets: [
            {
                backgroundColor: getCurrentTheme().palette.primary.main,
                data: etfSectorLabels.map((label) => etfs[label] || 0) as number[],
            },
            {
                backgroundColor: getCurrentTheme().palette.secondary.main,
                data: etfSectorLabels.map((label) => sectorData[sectorLabels.indexOf(label)] || 0) as number[],
            },
        ],
    };
}

export function formatModelChart(
    components: ModelComponent[],
    cashComponent: ModelComponent,
    single = true,
    targetComponents: ModelComponent[] = [],
): ChartData {
    const allocated = sumBy(components, (item) => item.weight);
    const targetAllocated = sumBy(targetComponents, (item) => item.weight);

    const labels: string[] = ['Cash'];
    const weights: number[] = [100 - allocated];
    const colors: string[] = [cashComponent.color];
    const targetWeights: number[] = [100 - targetAllocated];

    components.forEach((item) => {
        labels.push(item.symbol);
        weights.push(item.weight);
        colors.push(item.color);
    });

    targetComponents.forEach((item) => targetWeights.push(item.weight));

    const datasets = [
        {
            data: targetWeights,
            backgroundColor: colors,
            borderWidth: 0,
            hoverBorderWidth: 0,
            weight: 1,
        },
        {
            data: weights,
            backgroundColor: colors,
            borderWidth: 0,
            hoverBorderWidth: 0,
            weight: 3,
        },
    ];

    return {
        labels,
        datasets: single ? datasets.slice(1) : datasets,
    };
}

export function formatSliderStockCharts(data: QuoteHistory[], percentage?: number, range?: TimeRange): ChartData {
    const chartLineColor = (percentage: number) => {
        if (percentage === 0) {
            return getCurrentTheme().palette.primary.main;
        } else if (percentage > 0) {
            return getCurrentTheme().palette.common.green;
        } else if (percentage < 0) {
            return getCurrentTheme().palette.common.red;
        }
    };
    const chartBackgroundColor = (percentage: number) => {
        if (percentage === 0) {
            return alpha(getCurrentTheme().palette.primary.main, 0.05);
        } else if (percentage > 0) {
            return alpha(getCurrentTheme().palette.common.green, 0.05);
        } else if (percentage < 0) {
            return alpha(getCurrentTheme().palette.common.red, 0.05);
        }
    };

    return data.reduce<ChartData>(
        (result, item) => {
            result.labels?.push(formatDate(item.date, { withTime: range === TimeRange.OneDay ? true : false }));
            result.datasets[0]?.data.push(item.price || null);

            return result;
        },
        {
            labels: [],
            datasets: [
                {
                    data: [],
                    fill: true,
                    backgroundColor: percentage !== undefined ? chartBackgroundColor(percentage) : '',
                    borderColor: percentage !== undefined ? chartLineColor(percentage) : '',
                    pointHoverRadius: 0,
                    pointHitRadius: 0,
                    pointRadius: 0,
                    tension: 0.5,
                },
            ],
        },
    );
}

export function formatStockChart(data: QuoteStat, range: TimeRange, symbol: string, industryName?: string): ChartData {
    if (!data.length) {
        return EMPTY_LINE_CHART_DATA;
    }

    const change = formatPercentage(data[data.length - 1]?.value);
    const etfChange = formatPercentage(data[data.length - 1]?.etfValue);

    const dataset = [
        {
            data: data.map((item) => item.value),
            fill: false,
            borderColor: getCurrentTheme().palette.primary.light,
            backgroundColor: getCurrentTheme().palette.primary.light,
            pointHoverRadius: 3,
            pointHitRadius: 3,
            pointRadius: 1,
            label: industryName ? ` ${industryName}  ${change}` : ` ${symbol}  ${change}`,
            tension: 0.5,
        },
    ];

    if (data[0]?.etfValue) {
        dataset.push({
            data: data[0]?.etfValue ? data.map((item) => item.etfValue || 0) : [],
            fill: false,
            borderColor: getCurrentTheme().palette.success.main,
            backgroundColor: getCurrentTheme().palette.success.main,
            pointHoverRadius: 3,
            pointHitRadius: 3,
            pointRadius: 1,
            label:
                data[0]?.etfValue && symbol
                    ? ` ${symbol === ComparisonEtf.SPY ? ' Dow Jones ' : ' S&P 500 Index'}  ${etfChange}`
                    : '',
            tension: 0.5,
        });
    }

    return {
        labels: data.map((item) => formatDate(item.label, { withTime: range === TimeRange.OneDay ? true : false })),
        datasets: dataset,
    };
}

export function formatAccountPerformanceChart(balanceHistory: BalanceHistory[], fxRate: number): ChartData {
    return {
        labels: balanceHistory.map((item) => formatDate(item.date)),
        datasets: [
            {
                data: balanceHistory.map((item) => item.balance / fxRate),
                fill: true,
                backgroundColor: alpha(getCurrentTheme().palette.primary.main, 0.1),
                borderColor: getCurrentTheme().palette.primary.main,
                pointHoverRadius: 3,
                pointHitRadius: 3,
                pointRadius: 1,
                tension: 0.5,
            },
        ],
    };
}

export function formatExposureStockChart(holdings: PositionAccountWithExtraInfo[], symbol: string): ChartData {
    if (!holdings.length) {
        return EMPTY_CHART_DATA;
    }
    const exposure = multiply(
        sumBy(holdings, (position) => position.positionWeight),
        100,
    );

    return {
        labels: [symbol, 'Others'],
        datasets: [
            {
                data: [exposure, subtract(100, exposure)],
                backgroundColor: [getCurrentTheme().palette.primary.main, getCurrentTheme().palette.secondary.main],
                borderColor: getCurrentTheme().palette.text.primary,
            },
        ],
    };
}

export function formatAnalystsConsensusChart(consensus: TiprankOverviewWithExtraInfo): ChartData {
    return {
        labels: ['Buy', 'Hold', 'Sell'],
        datasets: [
            {
                data: [consensus.buy || 0, consensus.hold || 0, consensus.sell || 0],
                backgroundColor: [
                    getCurrentTheme().palette.consensus.buy,
                    getCurrentTheme().palette.consensus.hold,
                    getCurrentTheme().palette.consensus.sell,
                ],
            },
        ],
    };
}

export function formatStockAnalystsChart(data: QuoteHistory[], stats: Stats): ChartData {
    if (!data.length) {
        return EMPTY_CHART_DATA;
    }

    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

    const grouped = mapValues(
        groupBy(data, (data) => (data.date ? new Date(data.date).getMonth() : null)),
        (dates) =>
            divide(
                sumBy(dates, (date) => date.price || 0),
                dates.length,
            ),
    );

    const calculatePriceTargetLine = (start: number, end: number) => {
        const fraction = divide(subtract(end, start), 6);
        let point = start;
        const points: number[] = [];
        let counter = 1;

        while (counter < 7) {
            points.push(point);
            point += fraction;
            counter++;
        }
        points.push(end);
        return points;
    };

    const monthsLabels = Object.keys(grouped).map((item) => months[Number(item)]);
    const prices = Object.values(grouped);

    const lastMonth = monthsLabels[monthsLabels.length - 1]!;
    const lastPrice = prices[prices.length - 1]!;

    return {
        labels: [...monthsLabels, '', '', '', '', '', lastMonth],
        datasets: [
            {
                label: 'Past',
                data: prices,
                fill: true,
                backgroundColor: alpha(getCurrentTheme().palette.consensus.hold, 0.2),
                borderColor: getCurrentTheme().palette.consensus.hold,
                borderCapStyle: 'butt',
                borderDash: [],
                borderDashOffset: 0.0,
                borderJoinStyle: 'miter',
                pointBorderColor: getCurrentTheme().palette.consensus.hold,
                pointBackgroundColor: '#fff',
                pointBorderWidth: 2,
                pointHoverRadius: 2,
                pointHoverBackgroundColor: getCurrentTheme().palette.consensus.hold,
                pointHoverBorderColor: getCurrentTheme().palette.consensus.hold,
                pointHoverBorderWidth: 2,
                pointRadius: 3,
                pointHitRadius: 3,
                tension: 0.5,
            },
            {
                label: 'Low',
                data: [null, null, null, null, null, null, null, ...calculatePriceTargetLine(lastPrice, stats.low)],
                fill: false,
                borderColor: getCurrentTheme().palette.consensus.sell,
                borderCapStyle: 'butt',
                borderDash: [3],
                borderDashOffset: 0.0,
                borderJoinStyle: 'miter',
                pointBorderColor: getCurrentTheme().palette.consensus.sell,
                pointBackgroundColor: '#fff',
                pointBorderWidth: 0,
                pointHoverRadius: 0,
                pointHoverBackgroundColor: getCurrentTheme().palette.consensus.sell,
                pointHoverBorderColor: getCurrentTheme().palette.consensus.sell,
                pointHoverBorderWidth: 0,
                pointRadius: 0,
                pointHitRadius: 0,
                spanGaps: false,
                tension: 0.5,
            },
            {
                label: 'High',
                data: [null, null, null, null, null, null, null, ...calculatePriceTargetLine(lastPrice, stats.high)],
                fill: false,
                borderColor: getCurrentTheme().palette.consensus.buy,
                borderCapStyle: 'butt',
                borderDash: [3],
                borderDashOffset: 0.0,
                borderJoinStyle: 'miter',
                pointBorderColor: getCurrentTheme().palette.consensus.buy,
                pointBackgroundColor: '#fff',
                pointBorderWidth: 0,
                pointHoverRadius: 0,
                pointHoverBackgroundColor: getCurrentTheme().palette.consensus.buy,
                pointHoverBorderColor: getCurrentTheme().palette.consensus.buy,
                pointHoverBorderWidth: 0,
                pointRadius: 0,
                pointHitRadius: 0,
                spanGaps: false,
                tension: 0.5,
            },
            {
                label: 'Mid',
                data: [null, null, null, null, null, null, null, ...calculatePriceTargetLine(lastPrice, stats.average)],
                fill: false,
                borderColor: getCurrentTheme().palette.consensus.hold,
                borderCapStyle: 'butt',
                borderDash: [3],
                borderDashOffset: 0.0,
                borderJoinStyle: 'miter',
                pointBorderColor: getCurrentTheme().palette.consensus.hold,
                pointBackgroundColor: '#fff',
                pointBorderWidth: 0,
                pointHoverRadius: 0,
                pointHoverBackgroundColor: getCurrentTheme().palette.consensus.hold,
                pointHoverBorderColor: getCurrentTheme().palette.consensus.hold,
                pointHoverBorderWidth: 0,
                pointRadius: 0,
                pointHitRadius: 0,
                spanGaps: false,
                tension: 0.5,
            },
        ],
    };
}

const formatInOutData = (labels: string[], data: Record<string, Transfer[]>, deposit?: boolean): number[] => {
    return labels.map((label) =>
        sumBy(data[label] || [], (transfer) => {
            const isDeposit = transfer.direction === TransfersDirection.Incoming;

            if (deposit) {
                return isDeposit ? transfer.amount : 0;
            } else {
                return !isDeposit ? transfer.amount : 0;
            }
        }),
    );
};

const formatLabelsData = (
    transfers: Record<string, Transfer[]>,
    range: TimeRange,
): {
    labels: string[];
    dataIn: number[];
    dataOut: number[];
} => {
    const labels = sortBy(Object.keys(transfers), (label) => new Date(label).getTime());

    switch (range) {
        case TimeRange.OneMonth: {
            const dataForOneMonth = groupBy(transfers[labels[0]!], (label) =>
                formatDate(label.createdOn, { pattern: monthDayYearPattern }),
            );
            const labelsForOneMonth = sortBy(Object.keys(dataForOneMonth), (label) => new Date(label).getTime());
            return {
                labels: labelsForOneMonth,
                dataIn: formatInOutData(labelsForOneMonth, dataForOneMonth, true),
                dataOut: formatInOutData(labelsForOneMonth, dataForOneMonth, false),
            };
        }
        case TimeRange.ThreeMonth: {
            const dataForThreeMonth = pick(transfers, [labels[0]!, labels[1]!, labels[2]!]);
            const labelsForThreeMonth = sortBy(Object.keys(dataForThreeMonth), (label) => new Date(label).getTime());
            return {
                labels: labelsForThreeMonth,
                dataIn: formatInOutData(labelsForThreeMonth, dataForThreeMonth, true),
                dataOut: formatInOutData(labelsForThreeMonth, dataForThreeMonth, false),
            };
        }
        default: {
            return {
                labels,
                dataIn: formatInOutData(labels, transfers, true),
                dataOut: formatInOutData(labels, transfers, false),
            };
        }
    }
};

export function formatMoneyTransfersChart(transfers: Record<string, Transfer[]>, range: TimeRange): ChartData {
    if (isEmpty(transfers)) {
        return EMPTY_CHART_DATA;
    }

    const { labels, dataIn, dataOut } = formatLabelsData(transfers, range);

    return {
        labels,
        datasets: [
            {
                label: 'In',
                data: dataIn,
                backgroundColor: [alpha(getCurrentTheme().palette.common.green, 0.2)],
                borderColor: [getCurrentTheme().palette.common.green],
                borderWidth: 1,
                tension: 0.5,
            },
            {
                label: 'Out',
                data: dataOut,
                backgroundColor: [alpha(getCurrentTheme().palette.common.red, 0.2)],
                borderColor: [getCurrentTheme().palette.common.red],
                borderWidth: 1,
                tension: 0.5,
            },
        ],
    };
}
